import React from 'react';
import { Box, Typography, Chip } from '@mui/material';
import { formatPercent } from '../../utils/formatters';

/**
 * MarketStatusBar — Displays market indices, open/closed status, and regime.
 */
const MarketStatusBar = ({ indices, marketOpen, marketRegime }) => {
  const renderIndex = (symbol, data) => {
    if (!data) return null;
    const isPositive = data.changePercent >= 0;
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="body2" sx={{ fontWeight: 700, fontSize: 13 }}>
          {symbol}
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: 13 }}>
          ${data.price?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </Typography>
        <Typography
          variant="body2"
          sx={{ fontWeight: 600, fontSize: 13, color: isPositive ? 'success.main' : 'error.main' }}
        >
          {formatPercent(data.changePercent)}
        </Typography>
      </Box>
    );
  };

  const getRegimeColor = (regime) => {
    switch (regime) {
      case 'bull': return 'success';
      case 'cautious': return 'warning';
      case 'volatile': return 'warning';
      case 'bear': return 'error';
      default: return 'default';
    }
  };

  const getRegimeLabel = (regime) => {
    if (!regime) return null;
    const labels = { bull: '🐂 Bull', cautious: '⚠️ Cautious', volatile: '⚡ Volatile', bear: '🐻 Bear' };
    return labels[regime] || regime;
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        px: 2,
        py: 1.2,
        backgroundColor: 'background.paper',
        border: 1,
        borderColor: 'divider',
        borderRadius: 2.5,
        mb: 3,
        flexWrap: 'wrap',
      }}
    >
      {indices ? (
        <>
          {renderIndex('SPY', indices.SPY)}
          {renderIndex('QQQ', indices.QQQ)}
        </>
      ) : (
        <Typography variant="body2" sx={{ color: 'text.muted', fontSize: 13 }}>
          Market data unavailable
        </Typography>
      )}

      {marketOpen !== null && (
        <Chip
          label={marketOpen ? 'Market Open' : 'Market Closed'}
          size="small"
          color={marketOpen ? 'success' : 'default'}
          variant="outlined"
          sx={{ fontWeight: 600, fontSize: 11 }}
        />
      )}

      {marketRegime && (
        <Chip
          label={getRegimeLabel(marketRegime)}
          size="small"
          color={getRegimeColor(marketRegime)}
          variant="outlined"
          sx={{ fontWeight: 600, fontSize: 11 }}
        />
      )}
    </Box>
  );
};

export default MarketStatusBar;
